/**
 * Odd Muse desktop type-in search (after Cult Gaia).
 * - Focus opens the panel straight away with the default view already in the page.
 * - Typing fetches sections/om-search-inline.liquid and swaps it in, in the same frame.
 * - Speed: short debounce, every response cached, slow responses for old terms ignored,
 *   and the current view stays up while the next one loads, so it never flashes empty.
 *   Default-view images start loading as soon as the pointer reaches the field.
 */
class OmInlineSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.om-is__input');
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-om-is-status]');
    this.panel = this.querySelector('[data-om-is-panel]');
    if (!this.input || !this.form || !this.panel) return;

    this.defaultView = this.panel.querySelector('[data-om-is-default]');
    this.resultsView = this.panel.querySelector('[data-om-is-results]');
    this.cache = {};
    this.isOpen = false;
    this.mq = window.matchMedia('(min-width: 990px)');

    // The header can be transformed while it animates, which would trap a fixed panel inside it
    document.body.appendChild(this.panel);

    const warm = () => this.warmImages();
    this.addEventListener('pointerenter', warm, { once: true });
    this.input.addEventListener('focus', warm, { once: true });

    this.input.addEventListener('focus', () => {
      this.showView();
      this.open();
    });
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    });

    this.form.addEventListener('submit', (event) => {
      const term = this.term();
      if (!term) {
        event.preventDefault();
        this.input.focus();
        return;
      }
      this.remember(term);
    });

    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-om-is-close]')) {
        this.close();
        return;
      }
      const link = event.target.closest('a');
      if (link && link.dataset.omTerm) this.remember(link.dataset.omTerm);
    });

    document.addEventListener('click', (event) => {
      if (!this.isOpen) return;
      if (this.contains(event.target) || this.panel.contains(event.target)) return;
      this.close();
    });

    window.addEventListener(
      'scroll',
      () => {
        if (this.isOpen && Math.abs(window.scrollY - this.openScrollY) > 40) this.close();
      },
      { passive: true }
    );

    window.addEventListener('resize', () => {
      if (this.isOpen) this.position();
    });

    const onBreakpoint = () => {
      if (!this.mq.matches) this.close();
    };
    if (this.mq.addEventListener) this.mq.addEventListener('change', onBreakpoint);
  }

  term() {
    return this.input.value.trim();
  }

  key(term) {
    return term.toLowerCase().replace(/\s+/g, ' ');
  }

  remember(term) {
    if (window.OMRecentSearches && typeof window.OMRecentSearches.add === 'function') {
      window.OMRecentSearches.add(term);
    }
  }

  warmImages() {
    if (!this.defaultView) return;
    this.defaultView.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  // Default view when the field is empty; otherwise the latest results (cached ones show at once)
  showView() {
    const term = this.term();
    if (!term) {
      this.showDefault();
      return;
    }
    const cached = this.cache[this.key(term)];
    if (cached !== undefined) this.showResults(cached);
    else this.fetchResults(term);
  }

  showDefault() {
    if (this.controller) this.controller.abort();
    clearTimeout(this.busyTimer);
    this.panel.removeAttribute('aria-busy');
    if (this.defaultView) this.defaultView.hidden = false;
    this.resultsView.hidden = true;
  }

  showResults(markup) {
    this.resultsView.innerHTML = markup;
    this.resultsView.hidden = false;
    if (this.defaultView) this.defaultView.hidden = true;
    clearTimeout(this.busyTimer);
    this.panel.removeAttribute('aria-busy');

    const frame = this.resultsView.querySelector('[data-om-is-count]');
    const count = frame ? Number(frame.dataset.omIsCount) : 0;
    if (this.status) this.status.textContent = count > 0 ? `${count} pieces found` : 'No pieces found';
  }

  onInput() {
    clearTimeout(this.timer);
    const term = this.term();
    if (!term) {
      this.showDefault();
      return;
    }
    this.open();
    const cached = this.cache[this.key(term)];
    if (cached !== undefined) {
      this.showResults(cached);
      return;
    }
    this.timer = setTimeout(() => this.fetchResults(term), 90);
  }

  buildUrl(term) {
    const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const params = new URLSearchParams({
      q: term,
      section_id: 'om-search-inline',
      'resources[type]': 'query,collection,product',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,tag',
    });
    return `${base}?${params.toString()}`;
  }

  fetchResults(term) {
    const key = this.key(term);
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    // Only dim the current view if the answer is genuinely slow
    clearTimeout(this.busyTimer);
    this.busyTimer = setTimeout(() => this.panel.setAttribute('aria-busy', 'true'), 250);

    fetch(this.buildUrl(term), { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-om-search-inline');
        const markup = section ? section.innerHTML : '';
        this.cache[key] = markup;
        // Ignore a slow response for a term the customer has already typed past
        if (this.key(this.term()) === key) this.showResults(markup);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        clearTimeout(this.busyTimer);
        this.panel.removeAttribute('aria-busy');
      });
  }

  position() {
    const header = document.querySelector('sticky-header') || this.closest('.header');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.panel.style.top = `${bottom}px`;
    this.panel.style.maxHeight = `${window.innerHeight - bottom}px`;
  }

  open() {
    if (!this.mq.matches) return;
    this.position();
    if (this.isOpen) return;
    this.isOpen = true;
    this.openScrollY = window.scrollY;
    this.panel.hidden = false;
    requestAnimationFrame(() => this.panel.classList.add('is-open'));
    document.documentElement.classList.add('om-search-open');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    document.documentElement.classList.remove('om-search-open');
    this.input.setAttribute('aria-expanded', 'false');
    setTimeout(() => {
      if (!this.isOpen) this.panel.hidden = true;
    }, 180);
  }
}

if (!customElements.get('om-inline-search')) {
  customElements.define('om-inline-search', OmInlineSearch);
}
